import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const safeJsonParse = (value, fallback) => {
  try {
    if (!value) return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const fieldLabels = {
  jobNo: 'Job No',
  customer: 'Customer',
  product: 'Product',
  mainOperation: 'Main Operation',
  productionQty: 'Production Qty',
  wasteQty: 'Waste Qty',
};

const getFieldLabel = (field) => fieldLabels[field] || field;

const getChangedFieldLabels = (row) => {
  const changedFields = safeJsonParse(row?.changed_fields, []);

  if (!Array.isArray(changedFields)) return [];

  return changedFields.map((field) => ({
    raw: field,
    label: getFieldLabel(field),
  }));
};

const getValuePreview = (value, maxLength = 80) => {
  if (value === null || value === undefined || value === '') return '—';

  const text = String(value);

  if (text.length <= maxLength) return text;

  return `${text.substring(0, maxLength)}...`;
};

const SRI_LANKA_OFFSET_MINUTES = 330;

const padDatePart = (value) => String(value).padStart(2, '0');

const formatDateTimeParts = (date) => {
  const year = date.getUTCFullYear();
  const month = padDatePart(date.getUTCMonth() + 1);
  const day = padDatePart(date.getUTCDate());
  const hours = padDatePart(date.getUTCHours());
  const minutes = padDatePart(date.getUTCMinutes());
  const seconds = padDatePart(date.getUTCSeconds());

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const formatEditedAt = (value) => {
  if (!value) return '-';

  const text = String(value).trim();

  // Treat MySQL DATETIME values as server/UTC time and display Sri Lanka time.
  // This avoids browser timezone auto-conversion differences.
  const mysqlDateTimeMatch = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );

  if (mysqlDateTimeMatch) {
    const [, year, month, day, hour = '00', minute = '00', second = '00'] =
      mysqlDateTimeMatch;

    const timestamp = Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );

    return formatDateTimeParts(
      new Date(timestamp + SRI_LANKA_OFFSET_MINUTES * 60 * 1000)
    );
  }

  const parsedDate = new Date(text);

  if (Number.isNaN(parsedDate.getTime())) return text;

  return formatDateTimeParts(
    new Date(parsedDate.getTime() + SRI_LANKA_OFFSET_MINUTES * 60 * 1000)
  );
};

export default function MachineHourEditHistoryTab({
  supervisorId,
  allocatedMachines = [],
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [machineCode, setMachineCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageSeverity, setMessageSeverity] = useState('info');
  const [selectedHistoryRow, setSelectedHistoryRow] = useState(null);

  const tableHeaderBg = isDark
    ? alpha(theme.palette.primary.main, 0.22)
    : alpha(theme.palette.primary.main, 0.10);

  const tableHeaderText = isDark
    ? theme.palette.primary.light
    : theme.palette.primary.dark;

  const tableBorder = isDark
    ? alpha(theme.palette.primary.main, 0.35)
    : alpha(theme.palette.primary.main, 0.25);

  const tableHoverBg = isDark
    ? alpha(theme.palette.primary.main, 0.08)
    : alpha(theme.palette.primary.main, 0.04);

  const cardBg = theme.palette.background.paper;

  const machines = useMemo(() => {
    return allocatedMachines
      .map((machine) => ({
        code: machine.MachineCode || machine.code,
        description: machine.MachineDescription || machine.description || '',
        group: machine.MachineGroupCode || machine.groupCode || '',
      }))
      .filter((machine) => machine.code)
      .sort((a, b) => String(a.code).localeCompare(String(b.code)));
  }, [allocatedMachines]);

  const selectedRowChangedFields = useMemo(() => {
    if (!selectedHistoryRow) return [];

    return getChangedFieldLabels(selectedHistoryRow);
  }, [selectedHistoryRow]);

  const selectedRowOldValues = useMemo(() => {
    return safeJsonParse(selectedHistoryRow?.old_values, {});
  }, [selectedHistoryRow]);

  const selectedRowNewValues = useMemo(() => {
    return safeJsonParse(selectedHistoryRow?.new_values, {});
  }, [selectedHistoryRow]);

  const fetchHistory = async () => {
    if (!supervisorId) {
      setMessage('Supervisor ID is missing.');
      setMessageSeverity('error');
      return;
    }

    setLoading(true);
    setMessage('');
    setMessageSeverity('info');

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/machinesupervisor/job-summary/edit-history/${supervisorId}`,
        {
          params: {
            machineCode: machineCode || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          },
        }
      );

      const rows = response.data?.history || [];
      setHistoryRows(rows);

      if (rows.length === 0) {
        setMessage('No edit history found for the selected filters.');
        setMessageSeverity('info');
      }
    } catch (error) {
      setHistoryRows([]);
      setMessage(
        error.response?.data?.error ||
          error.message ||
          'Failed to load edit history.'
      );
      setMessageSeverity('error');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setMachineCode('');
    setStartDate('');
    setEndDate('');
    setHistoryRows([]);
    setMessage('');
    setMessageSeverity('info');
    setSelectedHistoryRow(null);
  };

  return (
    <Box
      sx={{
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        '@media (pointer: coarse)': {
          '& .MuiButton-root': {
            minHeight: 48,
            borderRadius: '12px',
            fontSize: 14,
          },
          '& .MuiInputBase-root': {
            minHeight: 48,
            fontSize: 14,
          },
          '& .MuiMenuItem-root': {
            minHeight: 48,
            fontSize: 14,
          },
          '& .MuiTableCell-root': {
            py: 1.45,
            fontSize: 14,
          },
          '& .MuiChip-root': {
            minHeight: 32,
            borderRadius: '10px',
          },
        },
      }}
    >
      <Card
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          border: '1px solid',
          borderColor: tableBorder,
          borderRadius: 3,
          backgroundColor: cardBg,
          backgroundImage: 'none',
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <HistoryIcon color="primary" />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              Machine-Hour Edit History
            </Typography>

            <Typography variant="body2" color="text.secondary">
              View job summary changes made for your allocated workstations.
            </Typography>
          </Box>
        </Stack>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Machine</InputLabel>

              <Select
                label="Machine"
                value={machineCode}
                onChange={(event) => setMachineCode(event.target.value)}
              >
                <MenuItem value="">All Allocated Machines</MenuItem>

                {machines.map((machine) => (
                  <MenuItem key={machine.code} value={machine.code}>
                    {machine.code} - {machine.description || machine.group}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="From Edited Date"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="To Edited Date"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SearchIcon />
                )
              }
              onClick={fetchHistory}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                minHeight: { xs: 48, md: 38 },
              }}
            >
              {loading ? 'Loading' : 'Search'}
            </Button>
          </Grid>

          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={clearFilters}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                minHeight: { xs: 48, md: 38 },
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Card>

      {message && (
        <Alert severity={messageSeverity} sx={{ mb: 2, borderRadius: 2 }}>
          {message}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: tableBorder,
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: cardBg,
            backgroundImage: 'none',
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 'calc(100vh - 340px)',
              overflowX: 'auto',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Table size="small" stickyHeader sx={{ minWidth: 1120 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 170,
                    }}
                  >
                    Edited At
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 110,
                    }}
                  >
                    Machine
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 110,
                    }}
                  >
                    Employee
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 240,
                    }}
                  >
                    Job
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 180,
                    }}
                  >
                    Edited By
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 300,
                    }}
                  >
                    Changed Fields
                  </TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 900,
                      backgroundColor: tableHeaderBg,
                      color: tableHeaderText,
                      borderColor: tableBorder,
                      minWidth: 130,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {historyRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      align="center"
                      sx={{
                        py: 5,
                        color: 'text.secondary',
                        borderColor: tableBorder,
                      }}
                    >
                      No edit history to display.
                    </TableCell>
                  </TableRow>
                ) : (
                  historyRows.map((row) => {
                    const changedFieldItems = getChangedFieldLabels(row);

                    return (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          '&:hover': {
                            backgroundColor: tableHoverBg,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            borderColor: tableBorder,
                          }}
                        >
                          {formatEditedAt(row.edited_at)}
                        </TableCell>

                        <TableCell sx={{ borderColor: tableBorder }}>
                          <Chip
                            size="small"
                            label={row.machine_code || '-'}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 800 }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            borderColor: tableBorder,
                            fontWeight: 700,
                          }}
                        >
                          {row.employee_id || '-'}
                        </TableCell>

                        <TableCell sx={{ borderColor: tableBorder }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                            {row.old_job_no || '-'} → {row.new_job_no || '-'}
                          </Typography>

                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            Start: {row.start_datetime || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ borderColor: tableBorder }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                            {row.edited_by || '-'}
                          </Typography>

                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            {row.edit_source || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ borderColor: tableBorder }}>
                          {changedFieldItems.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                              No field details available.
                            </Typography>
                          ) : (
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {changedFieldItems.slice(0, 4).map((field) => (
                                <Chip
                                  key={field.raw}
                                  label={field.label}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 800,
                                    borderRadius: 1.5,
                                    backgroundColor: isDark
                                      ? alpha(theme.palette.primary.main, 0.10)
                                      : alpha(theme.palette.primary.main, 0.05),
                                  }}
                                />
                              ))}

                              {changedFieldItems.length > 4 && (
                                <Chip
                                  label={`+${changedFieldItems.length - 4} more`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ fontWeight: 800 }}
                                />
                              )}
                            </Stack>
                          )}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            borderColor: tableBorder,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() => setSelectedHistoryRow(row)}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 800,
                              borderRadius: 2,
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={Boolean(selectedHistoryRow)}
        onClose={() => setSelectedHistoryRow(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            border: `1px solid ${tableBorder}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            pb: 1,
          }}
        >
          Changed Values
        </DialogTitle>

        <DialogContent dividers>
          {selectedHistoryRow && (
            <Stack spacing={2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: tableBorder,
                  backgroundColor: isDark
                    ? alpha(theme.palette.primary.main, 0.06)
                    : alpha(theme.palette.primary.main, 0.04),
                }}
              >
                <Grid container spacing={1.5}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Machine
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {selectedHistoryRow.machine_code || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Employee
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {selectedHistoryRow.employee_id || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Edited By
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {selectedHistoryRow.edited_by || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Edited At
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {formatEditedAt(selectedHistoryRow.edited_at)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Job
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {selectedHistoryRow.old_job_no || '-'} →{' '}
                      {selectedHistoryRow.new_job_no || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Start Datetime
                    </Typography>
                    <Typography variant="body2" fontWeight={800}>
                      {selectedHistoryRow.start_datetime || '-'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {selectedRowChangedFields.length === 0 ? (
                <Alert severity="info">No changed field details available.</Alert>
              ) : (
                selectedRowChangedFields.map((field) => (
                  <Paper
                    key={field.raw}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: tableBorder,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                      {field.label}
                    </Typography>

                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: alpha(theme.palette.error.main, 0.45),
                            backgroundColor: alpha(
                              theme.palette.error.main,
                              isDark ? 0.12 : 0.06
                            ),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark
                                ? theme.palette.error.light
                                : theme.palette.error.dark,
                              fontWeight: 900,
                            }}
                          >
                            Old
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              wordBreak: 'break-word',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {getValuePreview(selectedRowOldValues[field.raw], 500)}
                          </Typography>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: alpha(theme.palette.success.main, 0.45),
                            backgroundColor: alpha(
                              theme.palette.success.main,
                              isDark ? 0.12 : 0.06
                            ),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark
                                ? theme.palette.success.light
                                : theme.palette.success.dark,
                              fontWeight: 900,
                            }}
                          >
                            New
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              wordBreak: 'break-word',
                              color: theme.palette.text.primary,
                            }}
                          >
                            {getValuePreview(selectedRowNewValues[field.raw], 500)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setSelectedHistoryRow(null)}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              borderRadius: 2,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}